import React, { useState } from 'react';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';

export interface CommonModalProps {
    title: string;
    message: string | React.ReactNode;
    dataTestId: string;
    dismissButtonText?: string;
    size?: 'small' | 'large';
    hideCloseIcon?: boolean;
}

/**
 * Common Modal Component
 *
 * A reusable modal component that follows the established pattern
 * for modals in the application, similar to AlertBanner for alerts.
 *
 * @param title - Title displayed in modal header
 * @param message - Message content (string or React node)
 * @param dataTestId - Test ID for the modal
 * @param dismissButtonText - Optional custom text for dismiss button (default: "Okay")
 * @param size - Modal size (default: "small")
 * @param hideCloseIcon - Whether to hide the close icon in modal header (default: true)
 */
const CommonModal: React.FC<CommonModalProps> = ({
    title,
    message,
    dataTestId,
    dismissButtonText = 'Okay',
    size = 'small',
    hideCloseIcon = true
}) => {
    const [show, setShow] = useState(true);

    const handleDismiss = () => {
        setShow(false);
    };

    if (!show) {
        return null;
    }

    const header = <Typography variant="h4">{title}</Typography>;

    return (
        <Modal
            show={show}
            data-testid={dataTestId}
            footer={{
                primary: {
                    action: handleDismiss,
                    label: dismissButtonText,
                    'data-testid': `${dataTestId}-okay-button`
                }
            }}
            header={header}
            id={dataTestId}
            size={size}
            sx={
                hideCloseIcon
                    ? {
                          '& .css-rirz0g': {
                              display: 'none'
                          }
                      }
                    : undefined
            }
        >
            <Typography variant="body-sm">{message}</Typography>
        </Modal>
    );
};

export default CommonModal;
